import React, { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import { getCatalogoRegistro } from "../../registroEquipos/services/registroEquiposService";
import { cambiarModeloEquipoCentro } from "../services/centroEquiposService";

const campoSx = { "& .MuiInputBase-input": { fontSize: 13 } };

// Cambia el modelo de un equipo igual que se registra: se elige el tipo de modelo y el modelo del catálogo, y el texto queda
// "<tipo de modelo> <modelo>". Así no se escribe un modelo a mano ni se rompen los reportes que agrupan por modelo.
export default function CambiarModeloDialog({ open, tipo, equipoId, modeloActual, marcaActual, onClose, onGuardado }) {
  const [cat, setCat] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [idTipo, setIdTipo] = useState("");
  const [idModelo, setIdModelo] = useState("");
  const [marca, setMarca] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let activo = true;
    setCat(null); setError(""); setIdTipo(""); setIdModelo(""); setMarca(marcaActual || "");
    setCargando(true);
    getCatalogoRegistro(tipo)
      .then(({ data }) => {
        if (!activo) return;
        const c = data?.data ?? data;
        setCat(c);
        // Intenta dejar elegido el modelo actual: el texto guardado es "<tipo> <modelo>"
        const actual = String(modeloActual || "").trim().toLowerCase();
        const t = (c.tipos || []).filter((x) => actual.startsWith(String(x.nombre).toLowerCase())).sort((a, b) => b.nombre.length - a.nombre.length)[0];
        if (t) {
          setIdTipo(t.id);
          const resto = actual.slice(String(t.nombre).length).trim();
          const m = (c.modelos || []).find((x) => x.id_tipo === t.id && String(x.nombre).toLowerCase() === resto);
          if (m) setIdModelo(m.id);
        }
      })
      .catch((e) => activo && setError(e?.response?.data?.message || "No se pudo cargar el catálogo de modelos."))
      .finally(() => activo && setCargando(false));
    return () => { activo = false; };
  }, [open, tipo, modeloActual, marcaActual]);

  const modelos = useMemo(() => (cat?.modelos || []).filter((m) => m.id_tipo === idTipo), [cat, idTipo]);
  const tipoSel = (cat?.tipos || []).find((t) => t.id === idTipo);
  const modeloSel = modelos.find((m) => m.id === idModelo);
  const resultado = tipoSel && modeloSel ? `${tipoSel.nombre} ${modeloSel.nombre}`.trim() : "";
  const sinCambios = resultado === String(modeloActual || "").trim() && (marca || "").trim() === String(marcaActual || "").trim();

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await cambiarModeloEquipoCentro(tipo, equipoId, { id_tipo_modelo: idTipo, id_modelo: idModelo, marca });
      onGuardado();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo cambiar el modelo.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 800, py: 1 }}>Cambiar modelo</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.25 }}>
          Modelo actual: <b>{modeloActual || "—"}</b>{marcaActual ? <> · marca <b>{marcaActual}</b></> : null}
        </Typography>
        {cargando && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>}
        {cat && (
          <Box sx={{ display: "grid", gap: 1.25 }}>
            <TextField select size="small" label="Tipo de modelo" value={idTipo} onChange={(e) => { setIdTipo(e.target.value); setIdModelo(""); }} sx={campoSx}>
              {(cat.tipos || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Modelo" value={idModelo} onChange={(e) => setIdModelo(e.target.value)} disabled={!idTipo} sx={campoSx}
              helperText={idTipo && !modelos.length ? "Este tipo de modelo no tiene modelos en el catálogo" : " "}>
              {modelos.map((m) => <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>)}
            </TextField>
            <Autocomplete
              freeSolo size="small" options={cat.marcas || []} inputValue={marca} onInputChange={(_, v) => setMarca(v)}
              renderInput={(p) => <TextField {...p} label="Marca (opcional)" sx={campoSx} />}
            />
            <Alert severity={resultado ? "info" : "warning"} icon={false} sx={{ py: 0, fontSize: 12.5 }}>
              {resultado ? <>Quedará registrado como: <b>{resultado}</b></> : "Elige el tipo de modelo y el modelo."}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button size="small" variant="contained" onClick={guardar} disabled={guardando || !resultado || sinCambios}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
